import { timing, variantForLevel } from './params';
import { oppositeSide, SIDES, } from './types';
const GLYPHS = ['<', '>'];
export function makeStimulus(trialIdx, value, level) {
    const variant = variantForLevel(level);
    const cueSide = SIDES[Math.floor(Math.random() * SIDES.length)];
    const targetSide = oppositeSide(cueSide);
    const targetGlyph = GLYPHS[Math.floor(Math.random() * GLYPHS.length)];
    return {
        trialIdx,
        value: Math.max(0, value),
        cueSide,
        targetSide,
        targetGlyph,
        cueDurationMs: variant.cueDurationMs,
        targetDurationMs: variant.targetDurationMs,
    };
}
export function createAntisaccadeAdapter(level = 1) {
    let state = {
        subPhase: 'idle',
        stim: null,
        feedback: null,
        level,
        variant: variantForLevel(level),
    };
    const listeners = new Set();
    let resolver = null;
    let rejecter = null;
    let targetOnset = 0;
    const timers = [];
    function clearTimers() {
        while (timers.length > 0) {
            const t = timers.pop();
            if (t !== undefined)
                clearTimeout(t);
        }
    }
    function update(patch) {
        state = { ...state, ...patch };
        for (const l of listeners) {
            try {
                l(state);
            }
            catch {
                /* ignored */
            }
        }
    }
    function settle(picked, stim) {
        if (!resolver)
            return;
        const missed = picked === null;
        const rt = missed ? timing.responseWindowMs : Date.now() - targetOnset;
        const correct = !missed && picked === stim.targetSide;
        const r = resolver;
        resolver = null;
        rejecter = null;
        r({ pickedSide: picked, correct, reactionTimeMs: rt, missed });
    }
    return {
        mount() {
            // Idempotent.
        },
        unmount() {
            clearTimers();
            listeners.clear();
            if (rejecter) {
                rejecter(new Error('unmount'));
                rejecter = null;
                resolver = null;
            }
        },
        presentStimulus(stim) {
            clearTimers();
            return new Promise((resolve, reject) => {
                resolver = resolve;
                rejecter = reject;
                update({ subPhase: 'fixation', stim, feedback: null });
                // fixation → cue
                timers.push(setTimeout(() => {
                    update({ subPhase: 'cue' });
                    // cue → gap
                    timers.push(setTimeout(() => {
                        update({ subPhase: 'gap' });
                        // gap (= SOA value ms) → target
                        timers.push(setTimeout(() => {
                            update({ subPhase: 'target' });
                            targetOnset = Date.now();
                            // target → awaiting_input
                            timers.push(setTimeout(() => {
                                update({ subPhase: 'awaiting_input' });
                            }, stim.targetDurationMs));
                            // fenêtre de réponse à partir de l'onset du target
                            timers.push(setTimeout(() => {
                                settle(null, stim);
                            }, timing.responseWindowMs));
                        }, stim.value));
                    }, stim.cueDurationMs));
                }, timing.fixationMs));
            });
        },
        async showFeedback(correct) {
            update({
                subPhase: 'feedback',
                feedback: correct ? 'correct' : 'incorrect',
            });
            await new Promise((resolve) => {
                timers.push(setTimeout(() => {
                    update({ subPhase: 'iti' });
                    resolve();
                }, timing.feedbackMs));
            });
        },
        pause() {
            // Sprint 0.5 : no-op.
        },
        resume() {
            // Sprint 0.5 : no-op.
        },
        abort() {
            clearTimers();
            if (rejecter) {
                rejecter(new Error('aborted'));
                rejecter = null;
                resolver = null;
            }
        },
        getState() {
            return state;
        },
        subscribe(listener) {
            listeners.add(listener);
            listener(state);
            return () => {
                listeners.delete(listener);
            };
        },
        handleSide(side) {
            // Accepte les réponses dès l'onset du target (rapide) et pendant awaiting_input.
            if (state.subPhase !== 'target' &&
                state.subPhase !== 'awaiting_input') {
                return;
            }
            const stim = state.stim;
            if (!stim)
                return;
            clearTimers();
            settle(side, stim);
        },
        setLevel(newLevel) {
            const v = variantForLevel(newLevel);
            update({ level: newLevel, variant: v });
        },
    };
}
//# sourceMappingURL=adapter.js.map